
import { useEffect, useRef, useState } from 'react';
import { ArrowRight, ArrowUpRight } from 'lucide-react';
import MagneticButton from '../ui/MagneticButton';
import { scrambleText } from '../../lib/scramble';
import './HeroSection.css';

/* Styles moved to HeroSection.css — static import eliminates CLS from useEffect injection */
// (dead code below is safe to remove; keeping to minimize diff size)
const _DEAD_STYLES = `
  /* ── canvas — fixed background for the whole page ── */
  .hs-canvas {
    position:fixed; inset:0; width:100%; height:100%;
    z-index:0; pointer-events:none;
  }

  /* ── grain ──────────────────────────────────────────── */
  .hs-grain {
    position:fixed; top:-50%; left:-50%; width:200%; height:200%;
    z-index:1; opacity:.03; pointer-events:none;
    background-image:url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E");
    animation:hs-grain 2s steps(1) infinite;
  }
  @keyframes hs-grain {
    0%  {transform:translate(0,0)}    10%{transform:translate(-2%,-3%)}
    20% {transform:translate(3%,1%)}  30%{transform:translate(-1%,4%)}
    40% {transform:translate(4%,-2%)} 50%{transform:translate(-3%,3%)}
    60% {transform:translate(2%,-4%)} 70%{transform:translate(-4%,1%)}
    80% {transform:translate(1%,3%)}  90%{transform:translate(-2%,-1%)}
    100%{transform:translate(3%,-3%)}
  }

  /* ── scanlines ──────────────────────────────────────── */
  .hs-scanlines {
    position:fixed; inset:0; z-index:2; pointer-events:none;
    background:repeating-linear-gradient(
      0deg,transparent,transparent 2px,
      rgba(0,0,0,.04) 2px,rgba(0,0,0,.04) 4px
    );
  }

  /* ── vignette ───────────────────────────────────────── */
  .hs-vignette {
    position:fixed; inset:0; z-index:2; pointer-events:none;
    background:radial-gradient(ellipse at 50% 50%,
      transparent 45%, rgba(2,6,9,.75) 100%);
  }

  /* ── corner brackets ────────────────────────────────── */
  .hs-corner { position:fixed; z-index:10; width:22px; height:22px; opacity:.5; }
  .hs-corner--tl { top:22px;    left:22px;  border-top:1px solid #00e5ff; border-left:1px solid #00e5ff; }
  .hs-corner--tr { top:22px;    right:22px; border-top:1px solid #00e5ff; border-right:1px solid #00e5ff; }
  .hs-corner--bl { bottom:22px; left:22px;  border-bottom:1px solid #00e5ff; border-left:1px solid #00e5ff; }
  .hs-corner--br { bottom:22px; right:22px; border-bottom:1px solid #00e5ff; border-right:1px solid #00e5ff; }

  /* ── stats sidebar ──────────────────────────────────── */
  .hs-stats {
    position:fixed; right:7vw; top:50%; transform:translateY(-50%);
    z-index:10; display:flex; flex-direction:column; gap:26px;
    opacity:0; animation:hs-rise 1s ease 1s forwards;
  }
  @media(max-width:900px){ .hs-stats { display:none; } }
  .hs-stat { text-align:right; }
  .hs-stat-num {
    font-family:'Syne','Instrument Serif',serif; font-weight:700;
    font-size:28px; color:#f3fbff; line-height:1;
  }
  .hs-stat-label {
    font-family:'JetBrains Mono',monospace; font-size:9px;
    color:rgba(185,210,228,.65); letter-spacing:.14em;
    text-transform:uppercase; margin-top:3px;
  }
  .hs-stat-bar {
    margin-top:7px; height:1px;
    background:linear-gradient(to left,rgba(0,229,255,.3),transparent);
  }

  /* ── scroll hint ────────────────────────────────────── */
  .hs-scroll-hint {
    position:fixed; bottom:32px; left:50%; transform:translateX(-50%);
    z-index:10; display:flex; flex-direction:column; align-items:center; gap:7px;
    opacity:0; animation:hs-rise .8s ease 1.4s forwards;
  }
  .hs-scroll-hint span {
    font-family:'JetBrains Mono',monospace; font-size:9px;
    color:rgba(185,210,228,.55); letter-spacing:.18em; text-transform:uppercase;
  }
  .hs-scroll-line {
    width:1px; height:36px;
    background:linear-gradient(to bottom,#00e5ff,transparent);
    animation:hs-scroll-pulse 2.2s ease-in-out infinite;
  }
  @keyframes hs-scroll-pulse {
    0%,100%{opacity:.25;transform:scaleY(.8)} 50%{opacity:1;transform:scaleY(1)}
  }

  /* ── HUD ────────────────────────────────────────────── */
  .hs-hud {
    position:fixed; bottom:32px; right:7vw; z-index:10;
    font-family:'JetBrains Mono',monospace; font-size:9px;
    color:rgba(185,210,228,.45); letter-spacing:.1em; text-align:right;
    opacity:0; animation:hs-rise .8s ease 1.6s forwards; line-height:1.8;
  }
  .hs-hud-accent { color:rgba(0,229,255,.5); }

  /* ── hero section — IN SCROLL FLOW ─────────────────── */
  .hs-hero {
    position:relative;
    min-height:100vh;
    display:flex; align-items:center;
    padding:0 7vw;
    z-index:10;
  }

  /* ── hero content ───────────────────────────────────── */
  .hs-hero-content {
    max-width:640px;
    opacity:0; transform:translateY(28px);
    animation:hs-rise 1s ease .45s forwards;
  }
  @keyframes hs-rise { to { opacity:1; transform:translateY(0); } }

  /* ── badge ──────────────────────────────────────────── */
  .hs-badge {
    display:inline-flex; align-items:center; gap:8px;
    padding:6px 14px; margin-bottom:30px;
    border:1px solid rgba(0,229,255,.22); border-radius:100px;
    font-family:'JetBrains Mono',monospace; font-size:10px;
    color:#00e5ff; letter-spacing:.14em; text-transform:uppercase;
    backdrop-filter:blur(10px); background:rgba(0,229,255,.05);
  }
  .hs-badge-dot {
    width:5px; height:5px; border-radius:50%;
    background:#00e5ff; box-shadow:0 0 6px #00e5ff;
    animation:hs-pulse-dot 2s ease-in-out infinite; flex-shrink:0;
  }
  @keyframes hs-pulse-dot {
    0%,100%{opacity:1;transform:scale(1)} 50%{opacity:.3;transform:scale(.7)}
  }

  /* ── glitch headline ────────────────────────────────── */
  .hs-name1 {
    font-family:'Syne','Instrument Serif',serif; font-weight:800;
    font-size:clamp(40px,7.5vw,104px); line-height:.92;
    color:#fff; display:block; position:relative;
    word-break:normal; overflow-wrap:normal; max-width:none;
  }
  .hs-glitch {
    position:relative; display:inline-block;
    animation:hs-glitch-skew 7s infinite linear;
    word-break:normal; overflow-wrap:normal; max-width:none;
  }
  .hs-glitch::before,.hs-glitch::after {
    content:attr(data-text); position:absolute; top:0; left:0;
    width:100%; height:100%;
  }
  .hs-glitch::before { color:#00e5ff; animation:hs-glitch-a 7s infinite; }
  .hs-glitch::after  { color:#f0147c; animation:hs-glitch-b 7s infinite; }
  @keyframes hs-glitch-skew {
    0%,92%,100%{transform:skewX(0)}
    93%{transform:skewX(-2.5deg)} 95%{transform:skewX(1.5deg)} 97%{transform:skewX(-.5deg)}
  }
  @keyframes hs-glitch-a {
    0%,91%,100%{clip-path:inset(0 0 100% 0);transform:translate(0)}
    92%{clip-path:inset(8% 0 58% 0);transform:translate(-5px,1px);opacity:.9}
    93%{clip-path:inset(62% 0 4% 0);transform:translate(5px,-1px)}
    94%{clip-path:inset(38% 0 38% 0);transform:translate(-3px,2px)}
    95%{clip-path:inset(0 0 100% 0);transform:translate(0)}
  }
  @keyframes hs-glitch-b {
    0%,91%,100%{clip-path:inset(0 0 100% 0);transform:translate(0)}
    92%{clip-path:inset(52% 0 18% 0);transform:translate(5px,-1px);opacity:.8}
    93%{clip-path:inset(18% 0 63% 0);transform:translate(-5px,1px)}
    94%{clip-path:inset(73% 0 8% 0);transform:translate(3px,-2px)}
    95%{clip-path:inset(0 0 100% 0);transform:translate(0)}
  }
  .hs-name2 {
    font-family:'Syne','Instrument Serif',serif; font-weight:800;
    font-size:clamp(40px,7.5vw,104px); line-height:.92;
    display:inline-block; margin-bottom:28px;
    word-break:normal; overflow-wrap:normal; max-width:none;
    background:linear-gradient(110deg,#00e5ff 0%,#7c3aed 55%,#f0147c 100%);
    -webkit-background-clip:text; -webkit-text-fill-color:transparent;
    background-clip:text;
  }

  /* ── typewriter role ────────────────────────────────── */
  .hs-role {
    display:flex; align-items:center; gap:8px; margin-bottom:22px;
    font-family:'JetBrains Mono',monospace;
    font-size:clamp(12px,1.4vw,15px); color:rgba(185,210,228,.7);
  }
  .hs-role-prefix { color:#00e5ff; }
  .hs-role-cursor {
    display:inline-block; width:2px; height:1em;
    background:#00e5ff; vertical-align:middle;
    animation:hs-blink 1s step-end infinite;
  }
  @keyframes hs-blink { 0%,100%{opacity:1} 50%{opacity:0} }

  /* ── description ────────────────────────────────────── */
  .hs-desc {
    font-family:'JetBrains Mono',monospace; font-style:italic;
    font-size:clamp(11px,1.2vw,13px); line-height:1.9;
    color:rgba(185,210,228,.45); max-width:460px; margin-bottom:24px;
  }

  /* ── trust marks ────────────────────────────────────── */
  .hs-trust { display:flex; flex-wrap:wrap; gap:6px; margin-bottom:22px; }
  .hs-trust-chip {
    font-family:'JetBrains Mono',monospace; font-size:9px;
    padding:4px 10px; border-radius:100px; letter-spacing:.08em;
    border:1px solid rgba(0,229,255,.16); color:rgba(0,229,255,.5);
    background:rgba(0,229,255,.04);
  }

  /* ── capability tiles ───────────────────────────────── */
  .hs-caps { display:flex; flex-wrap:wrap; gap:1.1rem; margin-bottom:26px; }
  .hs-cap-tile { display:flex; flex-direction:column; gap:2px; }
  .hs-cap-label {
    font-family:'JetBrains Mono',monospace; font-size:9px;
    color:rgba(185,210,228,.5); letter-spacing:.12em; text-transform:uppercase;
  }
  .hs-cap-value {
    font-family:'Syne','Instrument Serif',serif; font-size:1.3rem;
    font-weight:700; color:#f3fbff; line-height:1;
  }

  /* ── stack tags ─────────────────────────────────────── */
  .hs-stack { display:flex; flex-wrap:wrap; gap:7px; margin-bottom:36px; }
  .hs-tag {
    font-family:'JetBrains Mono',monospace; font-size:10px;
    padding:5px 11px; border-radius:3px; letter-spacing:.07em; cursor:default;
    border:1px solid rgba(124,58,237,.3); color:rgba(167,139,250,.8);
    background:rgba(124,58,237,.07); transition:all .25s;
  }
  .hs-tag:hover {
    border-color:#7c3aed; color:#fff;
    background:rgba(124,58,237,.18);
    box-shadow:0 0 14px rgba(124,58,237,.35);
  }
`;

/* ─────────────────────────────────────────────────────────
   CONSTANTS
───────────────────────────────────────────────────────── */
const ROLES = [
  'Full-Stack Developer',
  'Systems Architect',
  'Flutter Engineer',
  'Cloud Architect',
  'UI / UX Engineer',
];

const STACK = [
  'React', 'Flutter', 'Node.js', 'Three.js',
  'Firebase', 'Supabase', 'Docker', 'Vercel',
];

const STATS = [
  { num: '10+', label: 'Projects'     },
  { num: '8+',  label: 'Tech domains' },
  { num: '2',   label: 'Founders'     },
];

/* ─────────────────────────────────────────────────────────
   HOOK — typewriter
───────────────────────────────────────────────────────── */
function useTypewriter(words) {
  const [text, setText] = useState('');
  const state = useRef({ rIdx: 0, cIdx: 0, deleting: false });

  useEffect(() => {
    let timer;
    const tick = () => {
      const { rIdx, cIdx, deleting } = state.current;
      const word = words[rIdx];
      if (!deleting) {
        const next = cIdx + 1;
        setText(word.slice(0, next));
        state.current.cIdx = next;
        if (next === word.length) {
          state.current.deleting = true;
          timer = setTimeout(tick, 2000);
        } else {
          timer = setTimeout(tick, 60 + Math.random() * 45);
        }
      } else {
        const next = cIdx - 1;
        setText(word.slice(0, next));
        state.current.cIdx = next;
        if (next === 0) {
          state.current.deleting = false;
          state.current.rIdx = (rIdx + 1) % words.length;
          timer = setTimeout(tick, 350);
        } else {
          timer = setTimeout(tick, 32);
        }
      }
    };
    timer = setTimeout(tick, 800);
    return () => clearTimeout(timer);
  }, [words]);

  return text;
}

/* ─────────────────────────────────────────────────────────
   COMPONENT
───────────────────────────────────────────────────────── */
export default function HeroSection({ content, scrollY, reducedMotion = false }) {
  const typed     = useTypewriter(ROLES);
  const [pastHero, setPastHero] = useState(false);
  const descRef = useRef(null);
  const statsRef = useRef(null);

  /* Hide fixed overlays once user scrolls past the hero viewport */
  useEffect(() => {
    if (!scrollY) return;
    const unsub = scrollY.on('change', (v) => {
      setPastHero(v > window.innerHeight * 0.85);
    });
    return unsub;
  }, [scrollY]);

  /* Scramble description text on mount */
  useEffect(() => {
    const el = descRef.current;
    if (!el || reducedMotion) return;
    const original = el.textContent;
    const cancel = scrambleText(el, original, { fps: 24 });
    return cancel;
  }, [reducedMotion]);

  /* Trigger counter animations when stats sidebar scrolls into view */
  useEffect(() => {
    if (reducedMotion) return;
    const el = statsRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          el.querySelectorAll('.hs-stat-num[data-count]').forEach((node) => {
            node.classList.add('is-counting');
          });
          observer.disconnect();
        }
      },
      { threshold: 0.5 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [reducedMotion]);

  /* safe fallbacks from content */
  const hero         = content?.hero ?? {};
  const description  = hero.description  ?? 'Crafting high-performance systems from database to deployment.';
  const trustMarks   = hero.trustMarks   ?? [];
  const capabilities = hero.capabilities ?? [];
  const ctaHref      = hero.cta?.href            ?? '#projects';
  const ctaLabel     = hero.cta?.label           ?? 'See our work';
  const secHref      = hero.secondaryCta?.href   ?? '#contact';
  const secLabel     = hero.secondaryCta?.label  ?? 'Start a project';

  return (
    <>
      {/* corner brackets */}
      <div className="hs-corner hs-corner--tl" aria-hidden="true" />
      <div className="hs-corner hs-corner--tr" aria-hidden="true" />
      <div className="hs-corner hs-corner--bl" aria-hidden="true" />
      <div className="hs-corner hs-corner--br" aria-hidden="true" />

      {/* stats sidebar — hidden when scrolled past hero */}
      {!pastHero && (
        <aside className="hs-stats" aria-label="Studio stats" ref={statsRef}>
          {STATS.map(({ num, label }) => (
            <div className="hs-stat" key={label}>
              <div className="hs-stat-num" data-count={num.replace('+', '')}>{num}</div>
              <div className="hs-stat-label">{label}</div>
              <div className="hs-stat-bar" />
            </div>
          ))}
        </aside>
      )}

      {/* scroll hint */}
      <div className="hs-scroll-hint" aria-hidden="true">
        <span>Scroll</span>
        <div className="hs-scroll-line" />
      </div>

      {/* HUD — hidden when scrolled past hero */}
      {!pastHero && (
        <div className="hs-hud" aria-hidden="true">
          <div><span className="hs-hud-accent">SYS</span> // SANJIVANI DEVWORKS</div>
          <div><span className="hs-hud-accent">ENV</span> // PRODUCTION</div>
          <div><span className="hs-hud-accent">LOC</span> // AHMEDABAD, IN</div>
        </div>
      )}

      {/* hero section — in scroll flow */}
      <section id="hero" className="hs-hero">
        <div className="hs-hero-content">

          {/* badge */}
          <div className="hs-badge">
            <span className="hs-badge-dot" />
            Full-Stack Studio · Ahmedabad, India
          </div>

          {/* glitch headline — h1 uses display:contents to preserve layout */}
          <h1 className="hs-headline" aria-label="SANJIVANI DEVWORKS.">
            <span className="hs-name1">
              <span className="hs-glitch" data-text="SANJIVANI" aria-hidden="true">SANJIVANI</span>
            </span>
            <span className="hs-name2" aria-hidden="true">DEVWORKS.</span>
          </h1>

          {/* typewriter */}
          <p className="hs-role">
            <span className="hs-role-prefix">&gt;_&nbsp;</span>
            <span>{typed}</span>
            <span className="hs-role-cursor" />
          </p>

          {/* description */}
          <p className="hs-desc" ref={descRef}>{description}</p>

          {/* trust marks */}
          {trustMarks.length > 0 && (
            <div className="hs-trust">
              {trustMarks.map((t) => (
                <span key={t} className="hs-trust-chip">{t}</span>
              ))}
            </div>
          )}

          {/* capability tiles */}
          {capabilities.length > 0 && (
            <div className="hs-caps">
              {capabilities.map((c) => (
                <div key={c.label} className="hs-cap-tile">
                  <span className="hs-cap-label">{c.label}</span>
                  <strong className="hs-cap-value">{c.value}</strong>
                </div>
              ))}
            </div>
          )}

          {/* stack tags */}
          <div className="hs-stack">
            {STACK.map((t) => (
              <span key={t} className="hs-tag">{t}</span>
            ))}
          </div>

          {/* CTAs */}
          <div className="button-row" style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap' }}>
            <MagneticButton
              href={ctaHref}
            >
              {ctaLabel}
              <ArrowRight size={15} />
            </MagneticButton>
            <MagneticButton
              href={secHref}
              variant="secondary"
            >
              {secLabel}
              <ArrowUpRight size={15} />
            </MagneticButton>
          </div>

        </div>
      </section>
    </>
  );
}
